import type { TemplatePage } from '../services/interface';

export const TEMPLATE_STUDIO_RUNTIME = 'template_studio' as const;
export const LEGACY_REACT_RUNTIME = 'legacy_react' as const;

export const TEMPLATE_STUDIO_RENDERERS = {
  html: 'html',
  image: 'image',
} as const;

export const TEMPLATE_STUDIO_AI_PROCESSES = {
  htmlSectionGeneration: 'html_section_generation',
  sectionImageGeneration: 'section_image_generation',
} as const;

const STANDARD_SECTION_MAP: Record<string, { type: string; pageId: string }> = {
  faq: { type: 'faqSection', pageId: 'faqs' },
  'cta-1': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  'cta-2': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  'cta-3': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  warranty: { type: 'guaranteeSection', pageId: 'guarantee_section' },
};

const definedMetadata = (metadata: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && value !== ''));

const sectionUuid = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;

type TemplateStudioFunnelSection = {
  id: string;
  enabled: boolean;
  order: number;
  isStandard?: boolean;
  sectionType?: string;
  hasHtmlContent?: boolean;
  templateDbId?: number;
  imageFormat?: string;
  colorMode?: string;
  primaryColor?: string;
  backgroundColor?: string;
  sectionInstructions?: unknown;
};

export type TemplateStudioFunnelConfig = {
  designTemplateType?: string | null;
  sections: TemplateStudioFunnelSection[];
};

export const buildTemplateStudioPages = (funnel: TemplateStudioFunnelConfig): TemplatePage[] => {
  const pages: TemplatePage[] = [];
  let sortIndex = 0;
  let ctaCount = 1;
  const templateId = funnel.designTemplateType || 'default';

  const enabledSections = funnel.sections
    .filter((section) => section.enabled)
    .sort((a, b) => a.order - b.order);

  for (const section of enabledSections) {
    if (!section.isStandard && section.sectionType) {
      if (section.hasHtmlContent) {
        const sectionUid = sectionUuid('custom_code_section');
        pages.push({
          id: sectionUid,
          name: sectionUid,
          type: 'customCodeSection',
          sort: sortIndex++,
          page: 'productPage',
          runtime: TEMPLATE_STUDIO_RUNTIME,
          renderer: TEMPLATE_STUDIO_RENDERERS.html,
          source: 'generate_html',
          metadata: definedMetadata({
            template_id: templateId,
            template_db_id: section.templateDbId,
            section_role: section.sectionType,
            ai_process_key: TEMPLATE_STUDIO_AI_PROCESSES.htmlSectionGeneration,
            section_instructions: section.sectionInstructions,
          }),
        });
      } else {
        const sectionUid = sectionUuid('custom_image_section');
        pages.push({
          id: sectionUid,
          name: sectionUid,
          type: 'customImageSection',
          sort: sortIndex++,
          page: 'productPage',
          runtime: TEMPLATE_STUDIO_RUNTIME,
          renderer: TEMPLATE_STUDIO_RENDERERS.image,
          source: 'generate_image',
          metadata: definedMetadata({
            template_id: templateId,
            template_db_id: section.templateDbId,
            section_role: section.sectionType,
            type_section_image: section.sectionType,
            ai_process_key: TEMPLATE_STUDIO_AI_PROCESSES.sectionImageGeneration,
            image_format: section.imageFormat,
            color_mode: section.colorMode,
            primary_color: section.primaryColor,
            background_color: section.backgroundColor,
          }),
        });
      }
    } else if (section.isStandard) {
      const mapping = STANDARD_SECTION_MAP[section.id];
      if (!mapping) continue;

      if (mapping.type === 'buyNowFreeShippingSection') {
        ctaCount++;
        pages.push({
          id: `${mapping.pageId}_${ctaCount}`,
          name: `${mapping.pageId}_${ctaCount}`,
          type: mapping.type,
          sort: sortIndex++,
          page: 'productPage',
          runtime: LEGACY_REACT_RUNTIME,
          renderer: 'react',
        });
      } else {
        pages.push({
          id: mapping.pageId,
          name: mapping.pageId,
          type: mapping.type,
          sort: sortIndex++,
          page: 'productPage',
          runtime: LEGACY_REACT_RUNTIME,
          renderer: 'react',
        });
      }
    }
  }

  pages.push(
    {
      id: 'header_thankyou',
      name: 'header_thankyou',
      type: 'headerThankyouSection',
      sort: 0,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
      metadata: {
        ai_process_key: 'thankyou_header_content',
      },
    },
    {
      id: 'product_list',
      name: 'product_list',
      type: 'productListSection',
      sort: 1,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
    },
    {
      id: 'button_thankyou',
      name: 'button_thankyou',
      type: 'buttonThankyouSection',
      sort: 2,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
    },
  );

  return pages;
};
